import React, {useEffect} from 'react';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToast from '../../common/arr-toast/ArrToast';
import {ToastType} from '../../../shared/constants';
import {ReactComponent as WarningIcon} from '../../../assets/images/warning-toast.svg';
import {ReactComponent as SuccessIcon} from '../../../assets/images/success-toast.svg';
import {ReactComponent as ErrorIcon} from '../../../assets/images/error-toast.svg';
import {ReactComponent as CloseToast} from '../../../assets/images/close-toast.svg';
import {useAddNavigationNotification} from '../../../hooks/document/useDocument';
import {t} from 'i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import {Box, FormControlLabel} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';

interface ViewResultsModalProps {
  continueModal: boolean;
  masterDocId: number;
  setContinueModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const getStartIconAndTitle = (type: ToastType) => {
  switch (type) {
    case ToastType.Success:
      return {icon: <SuccessIcon />, title: 'Success'};
    case ToastType.Error:
      return {icon: <ErrorIcon />, title: 'Error'};
    case ToastType.Warning:
      return {icon: <WarningIcon />, title: ''};
    default:
      return {icon: <WarningIcon />, title: ''};
  }
};

const ViewResultsModal = ({
  continueModal,
  masterDocId,
  setContinueModal,
}: ViewResultsModalProps) => {
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showAgain, setShowAgain] = React.useState<boolean>(false);
  const [doNotShowAgain, setDoNotShowAgain] = React.useState<boolean>(false);
  const [toastDetails, setToastDetails] = React.useState<{
    show: boolean;
    toastStatus: ToastType | null;
    toastMessage: string;
  }>({show: false, toastStatus: null, toastMessage: ''});

  const payload = {
    navigationId: masterDocId,
    notificationType: 'ADD',
    notificationTypeCode: '',
    showNotification: 0,
  };

  const {mutate: addNavigationNotification} = useAddNavigationNotification();

  const intervalId = React.useRef<NodeJS.Timeout | null>(null);
  const pollingTime = React.useRef<number>(9000);

  useEffect(() => {
    if (continueModal) {
      handleInterval();
    }
    return () => {
      handleClearInterval();
    };
  }, [continueModal]);

  function handleInterval() {
    const currentTime = Date.now();
    setLoading(true);
    intervalId.current = setInterval(() => {
      const elapsedTime = Date.now() - currentTime;
      if (elapsedTime >= pollingTime.current) {
        if (doNotShowAgain) {
          sendToBackground(ToastType.Warning);

          handleCancel();
        } else {
          setLoading(false);
          setShowModal(true);
        }
        handleClearInterval();
      }
    }, 2000);
  }

  function handleClearInterval() {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
  }

  const handleYes = () => {
    handleInterval();
    setShowAgain(true);
    setShowModal(false);
    setLoading(true);
  };
  const handleNo = () => {
    sendToBackground(ToastType.Success);
    handleCancel();
  };
  const handleCancel = () => {
    setShowModal(false);
    setLoading(false);
    pollingTime.current = 9000;
    setShowAgain(false);
    setDoNotShowAgain(false);
    setContinueModal(false);
  };
  const handleDoNotShowAgain = (value: boolean) => {
    setDoNotShowAgain(value);
    if (value) {
      pollingTime.current = 30000;
    }
  };

  function sendToBackground(type: ToastType) {
    addNavigationNotification(payload, {
      onSuccess: (data) => {
        console.log('data', data);
        setToastDetails({
          show: true,
          toastStatus: type,
          toastMessage: t('categorization.bulkNavigationModal.toastMsg', {
            masterDocId: masterDocId,
          }),
        });
      },
      onError: () => {
        setToastDetails({
          show: true,
          toastStatus: ToastType.Error,
          toastMessage: t('categorization.docListModal.toastErrorMsg', {
            masterDocId: masterDocId,
          }),
        });
      },
    });
  }
  const {icon, title} = getStartIconAndTitle(toastDetails.toastStatus!);
  return (
    <>
      {loading && (
        <ArrLoadingSpinner show={loading} overlay={true} isOpen={true} />
      )}
      {showModal && (
        <ArrModal
          showModal={showModal}
          className="modal-box"
          title={t('categorization.bulkNavigationModal.title')}
          description={t('categorization.bulkNavigationModal.description')}
          showClose={true}
          dataTestId="view-results-modal"
          buttons={[
            {
              id: 'no',
              label: t('common.no'),
              onClick: handleNo,
              variant: 'secondary',
              dataTestId: 'vm-no-btn',
            },
            {
              id: 'yes',
              label: t('common.yes'),
              onClick: handleYes,
              variant: 'primary',
              dataTestId: 'vm-yes-btn',
            },
          ]}
          footer={
            <Box className="cancel-row">
              <ArrButton
                variant="text"
                onClick={handleCancel}
                size="medium"
                dataTestId="view-results-modal-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
            </Box>
          }
          onClose={handleCancel}
          autoFocusCloseButton={true}
        >
          {showAgain && (
            <Box className="modal-actions" data-testid="do-not-show-again">
              <FormControlLabel
               data-testid={`consent-text ${t('waitToComplete.doNotPrompt')}`}
                className="consent-text"
                control={
                  <ArrCheckbox
                    checked={doNotShowAgain}
                    onChange={handleDoNotShowAgain}
                    aria-label={`do-not-show-again-checkbox`}
                  />
                }
                classes={{label: 'consent-text'}}
                label={t('waitToComplete.doNotPrompt')}
              />
            </Box>
          )}
        </ArrModal>
      )}
      {toastDetails.show && (
        <ArrToast
          id="status-toast"
          showStartIcon={true}
          duration={5000}
          startIcon={icon}
          title={title}
          type={toastDetails.toastStatus!}
          message={toastDetails.toastMessage}
          dismissable
          onClose={() => setToastDetails({...toastDetails, show: false})}
          position="bottom-right"
          closeIcon={<CloseToast />}
        />
      )}
    </>
  );
};

export default ViewResultsModal;
